<!--
  - Copyright (c) 2023. Haodong JU
  -->

<template>
  <div>
    <page-header class="py-4 pb-6">
      {{ $t('TaxType') }}
    </page-header>
    <loading-provider :loading="loading">
      <div
        class="tax-type-grid"
      >
        <v-card
          v-for="(item,index) in taxList"
          :key="item.id"
          class="text-body-1 mt-4 px-6 py-4 d-flex flex-column"
          elevation="0"
          @click="editItem(item,index)"
        >
          <div
            class="flex-grow-1 d-flex flex-column overflow-hidden"
            style="border-radius: 16px; min-height: 0"
          >
            <div class="d-flex justify-space-between align-center py-4 flex-shrink-0">
              <div
                class="text-h3 font-weight-bold"
              >
                {{ item?._langsname }}
              </div>
              <div class="font-weight-bold">
                {{ item.id }}
              </div>
            </div>
            <div class="tax-type-scroll-wrap flex-grow-1">
              <div
                :ref="'taxList-' + item.id"
                :style="hasScrollHint(item.id) ? { paddingBottom: '44px' } : null"
                class="tax-type-list py-4 overflow-y-auto"
                @scroll="onCardScroll(item.id)"
              >
                <v-card
                  v-for="i in item.taxList"
                  :key="i.id"
                  class="px-4 py-2 mt-4 tax-type-row"
                  color="grey lighten-5"
                  elevation="0"
                  @click.stop="openRowDialog(i)"
                >
                  <div class="text-right d-flex align-center">
                    <div class="text-body-1 text-truncate">
                      {{ i.consumeTypeName }}
                    </div>
                    <v-spacer />
                    <div class="flex-shrink-0">
                      {{ (i.taxRateValue * 100).toFixed(2) }}%
                    </div>
                    <v-icon
                      class="tax-type-row-edit ml-2 flex-shrink-0"
                      size="16"
                    >
                      mdi-pencil
                    </v-icon>
                  </div>
                </v-card>
              </div>
              <transition name="fade">
                <div
                  v-if="hasScrollHint(item.id)"
                  class="tax-type-scroll-hint"
                  @click.stop="scrollCardDown(item.id)"
                >
                  <v-icon size="18">
                    mdi-chevron-down
                  </v-icon>
                </div>
              </transition>
            </div>
          </div>
        </v-card>
      </div>
    </loading-provider>
    <ik-entity-form
      v-model="showTaxDialog"
      :edited-index="taxIndex"
      :model="model"
      :title="$t('menu')"
      @need-refresh="reload"
    />
    <v-dialog
      v-model="taxRowDialog"
      max-width="440"
      persistent
      :fullscreen="$vuetify.breakpoint.mobile"
    >
      <v-card
        :style="$vuetify.breakpoint.mobile ? null : 'border-radius: 20px'"
        :tile="$vuetify.breakpoint.mobile"
        class="pa-6 pa-sm-8"
      >
        <loading-provider :loading="loading">
          <div class="d-flex align-center">
            <v-avatar
              class="flex-shrink-0"
              color="amber lighten-4"
              size="44"
            >
              <v-icon color="amber darken-3">
                mdi-percent-box-outline
              </v-icon>
            </v-avatar>
            <div class="ml-4 overflow-hidden">
              <div class="text-h5 font-weight-medium text-truncate">
                {{ $t('TaxRate') }}
              </div>
              <div class="text-body-2 grey--text text--darken-1 text-truncate">
                {{ rowConsumeTypeName }}
              </div>
            </div>
            <v-spacer />
            <v-btn
              class="flex-shrink-0"
              icon
              large
              @click="taxRowDialog = false"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>

          <v-card
            class="d-flex align-center mt-6 px-4 py-3 rounded-lg"
            color="#f6f6f6"
            elevation="0"
          >
            <div class="text-body-2 grey--text text--darken-1 flex-shrink-0">
              {{ $t('TaxType') }}
            </div>
            <v-spacer />
            <div class="text-body-1 font-weight-medium text-truncate">
              {{ rowTaxTypeName }}
            </div>
          </v-card>

          <v-form
            ref="taxRowForm"
            v-model="taxRowValid"
            class="mt-6"
            lazy-validation
          >
            <v-text-field
              v-model="taxRowRate"
              :rules="taxRowRules"
              autofocus
              class="tax-rate-input"
              hide-details="auto"
              outlined
              suffix="%"
              type="number"
              @keyup.enter="saveRowTax"
            />
          </v-form>

          <div class="d-flex justify-center align-center mt-8">
            <v-btn
              :disabled="!taxRowValid"
              class="amber lighten-4 flex-grow-1 mr-4"
              depressed
              height="48"
              rounded
              @click="saveRowTax"
            >
              <v-icon
                class="mr-2"
                small
              >
                mdi-check
              </v-icon>
              {{ $t('save') }}
            </v-btn>
            <v-btn
              class="flex-grow-1"
              height="48"
              outlined
              rounded
              @click="taxRowDialog = false"
            >
              {{ $t('cancel') }}
            </v-btn>
          </div>
        </loading-provider>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>

import CategoryType from '@/model/dataLayer/api/dish/CategoryType'
import Tax from '@/model/dataLayer/api/export/Tax'
import pageHeader from '@/views/widget/typography/PageHeader'
import LoadingProvider from '@/views/widget/LoadingProvider'
import IkEntityForm from '@/plugins/EntityFrameWork/IkEntityForm.vue'

export default {
  components: {
    LoadingProvider,
    pageHeader,
    IkEntityForm
  },
  data: function () {
    return ({
      togoTaxRate: '',
      IHTaxRate: '',
      tax: [],
      rules: [
        v => !!v || this.$t('TaxIsRequired')
      ],
      valid: true,
      loading: false,
      taxSettingList: [],
      taxSettingDialog: false,
      taxRowDialog: false,
      taxRowItem: null,
      taxRowRate: '',
      taxRowValid: true,
      scrollHints: {},
      taxRowRules: [
        v => (v !== '' && v !== null && v !== undefined) || this.$t('TaxIsRequired'),
        v => (!isNaN(Number(v)) && Number(v) >= 0) || this.$t('TaxIsRequired')
      ],
      taxItem: [],
      taxIndex: null,
      showTaxDialog: false,
      taxList: [],
      model: CategoryType
    })
  },
  computed: {
    rowConsumeTypeName () {
      return this.taxRowItem?.consumeTypeName || ''
    },
    rowTaxTypeName () {
      const row = this.taxRowItem
      if (!row) return ''
      if (row.dishCategoryTypeName) return row.dishCategoryTypeName
      const category = this.taxList.find(it => it.id === row.dishesCategoryTypeId)
      return category?._langsname || ''
    }
  },
  mounted () {
    this.reload()
    window.addEventListener('resize', this.refreshScrollHints)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.refreshScrollHints)
  },
  watch: {
    loading (val) {
      if (!val) this.$nextTick(this.refreshScrollHints)
    }
  },
  methods: {
    getScrollEl (id) {
      const el = this.$refs['taxList-' + id]
      return Array.isArray(el) ? el[0] : el
    },
    hasScrollHint (id) {
      const hint = this.scrollHints[id]
      return !!hint && hint.overflow && !hint.atBottom
    },
    updateScrollHint (id) {
      const el = this.getScrollEl(id)
      if (!el) return
      const overflow = el.scrollHeight - el.clientHeight > 8
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 8
      const current = this.scrollHints[id]
      if (current && current.overflow === overflow && current.atBottom === atBottom) return
      this.$set(this.scrollHints, id, { overflow, atBottom })
    },
    onCardScroll (id) {
      this.updateScrollHint(id)
    },
    refreshScrollHints () {
      this.taxList.forEach(item => this.updateScrollHint(item.id))
    },
    scrollCardDown (id) {
      const el = this.getScrollEl(id)
      if (!el) return
      el.scrollTo({
        top: el.scrollTop + Math.max(el.clientHeight * 0.8, 80),
        behavior: 'smooth'
      })
    },
    openRowDialog (taxItem) {
      this.taxRowItem = taxItem
      console.log(this.taxRowItem, 'item')
      this.taxRowRate = (Number(taxItem.taxRateValue) * 100).toFixed(2)
      this.taxRowValid = true
      this.taxRowDialog = true
    },
    async saveRowTax () {
      if (this.$refs.taxRowForm && !this.$refs.taxRowForm.validate()) return
      if (!this.taxRowItem) return
      this.loading = true
      try {
        await Tax.edit({
          ...this.taxRowItem,
          taxRateValue: Number(this.taxRowRate) / 100
        })
        this.taxRowDialog = false
        await this.reload()
      } finally {
        this.loading = false
      }
    },
    async getRealTaxListById (id) {
      this.taxSettingList = this.tax.filter(it => it.dishesCategoryTypeId === id)
    },
    async saveAll () {
      this.loading = true
      for (const item of this.taxSettingList) {
        if (item.consumeTypeId === 2) {
          item.taxRateValue = this.togoTaxRate / 100
        } else {
          item.taxRateValue = this.IHTaxRate / 100
        }
        await Tax.edit(item)
      }
      this.loading = false
      this.taxSettingDialog = false
      await this.reload()
    },
    async settingTax (item) {
      this.taxSettingDialog = true
      this.loading = true
      this.IHTaxRate = (item.taxList.find(it => it.consumeTypeId === 1).taxRateValue * 100).toFixed(2)
      this.togoTaxRate = (item.taxList.find(it => it.consumeTypeId === 2).taxRateValue * 100).toFixed(2)
      this.taxItem = item
      this.taxSettingList = (await Tax.getList(true)).filter(it => it.dishesCategoryTypeId === item.id)
      this.loading = false
    },
    async editItem (item, index) {
      this.taxItem = item
      this.taxIndex = index
      this.showTaxDialog = true
    },
    async reload () {
      this.loading = true
      this.tax = (await Tax.getList(true))
      this.taxList = (await CategoryType.getList(true))
      this.taxList.forEach(item => {
        item.taxList = this.tax.filter(it => it.dishesCategoryTypeId === item.id)
      })
      console.log(this.taxList, 'list')
      this.loading = false
    }
  }
}
</script>

<style scoped>
.tax-type-grid {
  display: grid;
  grid-gap: 16px;
  grid-auto-rows: minmax(360px, auto);
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
}

.tax-type-grid > .v-card {
  max-height: 520px;
  min-height: 0;
  overflow: hidden;
}

.tax-type-grid > .v-card .text-h3 {
  line-height: 1.2;
  overflow-wrap: anywhere;
}

.tax-type-row {
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.tax-type-row:hover {
  background-color: #eef1f5 !important;
}

.tax-type-row-edit {
  opacity: 0;
  transition: opacity 0.2s ease;
}

.tax-type-row:hover .tax-type-row-edit {
  opacity: 0.6;
}

.tax-type-scroll-wrap {
  position: relative;
  min-height: 0;
}

.tax-type-scroll-hint {
  position: absolute;
  left: 50%;
  bottom: 6px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  color: rgba(0, 0, 0, 0.55);
  background-color: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
  cursor: pointer;
  transform: translateX(-50%);
  animation: tax-hint-bounce 1.6s ease-in-out infinite;
}

.tax-type-scroll-hint:hover {
  color: rgba(0, 0, 0, 0.85);
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.25);
}

@keyframes tax-hint-bounce {
  0%, 100% {
    transform: translate(-50%, 0);
  }
  50% {
    transform: translate(-50%, 4px);
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.tax-rate-input >>> .v-input__slot {
  min-height: 76px;
  border-radius: 14px;
  background-color: #fff;
}

.tax-rate-input >>> input {
  height: 44px;
  padding-top: 0;
  padding-bottom: 0;
  font-size: 34px;
  font-weight: 600;
  text-align: center;
}

.tax-rate-input >>> .v-text-field__suffix {
  padding-left: 6px;
  font-size: 22px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.45);
}

.tax-rate-input >>> input::-webkit-outer-spin-button,
.tax-rate-input >>> input::-webkit-inner-spin-button {
  margin: 0;
  -webkit-appearance: none;
}

.tax-rate-input >>> input[type='number'] {
  -moz-appearance: textfield;
}

.tax-rate-input >>> .v-messages {
  font-size: 12px;
  text-align: center;
}

.tax-type-list {
  height: 100%;
  min-height: 0;
  overscroll-behavior: contain;
  scrollbar-width: thin;
}

.tax-type-list::-webkit-scrollbar {
  width: 6px;
}

.tax-type-list::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

.tax-type-list::-webkit-scrollbar-thumb:hover {
  background-color: rgba(0, 0, 0, 0.35);
}

.tax-type-list::-webkit-scrollbar-track {
  background-color: transparent;
}
</style>
